// Phone-number normalisation shared by every place the console accepts a
// number destined for a device shadow (button contacts, the inbound
// allowlist, and schema-driven `phone` / `phone_array` fields).
//
// The problem this solves: numbers are almost always pasted, not typed, and
// the common sources wrap them in characters that are invisible on screen.
// iOS/macOS Contacts, WhatsApp and Outlook emit Unicode bidi controls
// (U+202A ... U+202C) around a number so the leading "+" renders on the
// correct side in right-to-left locales. Those code points are zero-width and
// are category Cf (format), not whitespace, so `String.prototype.trim()`
// leaves them in place and every phone pattern we validate against fails.
// Because they render as nothing, the resulting error quotes a number that
// looks perfectly valid, which is impossible to act on.

// The character classes are deliberately expressed as Unicode properties
// rather than hand-listed ranges, so this matches the Go sanitiser
// (backend/modules/devicetype/validation.go) code point for code point:
// `\p{White_Space}` is exactly Go's `unicode.IsSpace`, and `\p{Cf}` is
// exactly `unicode.Is(unicode.Cf, r)`. A hand-rolled range list drifted from
// the Go set by 150 code points, which meant the client rejected input the
// server would happily have accepted — reproducing the original confusing
// rejection for anyone whose contacts app emits, say, U+061C (Arabic letter
// mark). The residual risk is Unicode-table version skew between the Go
// runtime and the browser, which moves a handful of code points at most.
//
// The trailing members are the readability separators: full stop, hyphen-minus,
// the typographic dash family (U+2010 hyphen through U+2015 horizontal bar)
// and the minus sign.
const strippablePattern = /[\p{White_Space}\p{Cf}.\-\u{2010}-\u{2015}\u{2212}]/gu;

// sanitisePhoneNumber strips formatting noise so a pasted number validates as
// the digits the user can actually see.
//
// Deliberately NOT stripped: brackets. `+27 (0)82 796 4840` is a real paste
// format where the bracketed trunk prefix must be dropped, not flattened.
// Silently turning it into `+270827964840` would produce a well-formed but
// wrong number, and these numbers are dialled by an SOS button, so brackets
// survive, fail validation, and the user is asked to resolve the ambiguity.
export function sanitisePhoneNumber(raw: string): string {
  return raw.replace(strippablePattern, "");
}

// looksLikeMultipleNumbers reports input that is more than one number, which
// must be rejected before separators are stripped.
//
// This is the bracket rule again, arriving through a different door. Stripping
// the space from "0821234567 0829876543" yields "08212345670829876543" — a
// well-formed 20-digit value. Without this guard (and the digit ceiling in the
// patterns that consume it) a pasted pair silently collapses into a single
// number that is nobody's, and the number the operator meant to allow is not
// on the list at all. Two numbers have to fail loudly rather than merge into
// one dialable wrong number.
export function looksLikeMultipleNumbers(raw: string): boolean {
  return /[\n\r]/.test(raw) || (raw.match(/\+/g)?.length ?? 0) > 1;
}

// describePhoneForError renders a value safe to quote in an error message,
// escaping anything outside printable ASCII to `U+XXXX`. Without this, a
// number rejected for an invisible character quotes back looking correct.
export function describePhoneForError(raw: string): string {
  return raw.replace(/[^ -~]/gu, (character) => {
    const code = character.codePointAt(0) ?? 0;
    return `U+${code.toString(16).toUpperCase().padStart(4, "0")}`;
  });
}
